import React, { useState } from "react";
import {
  Card,
  Space as AntdSpace,
  Button,
  Table,
  Typography,
  Row,
  Col,
  Statistic,
  Tag,
  Input,
  DatePicker,
  Select,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  useReportsServiceDownloadReparationReportV2,
  useReportsServiceGetReparationReportV2,
} from "api/queries";
import { useSelector } from "react-redux";

const { Title } = Typography;

const PERIOD_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "This Week", value: "This Week" },
  { label: "This Month", value: "This Month" },
  { label: "This Year", value: "This Year" },
];

const ReparationCaseSummary = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [periodFilter, setPeriodFilter] = useState("Custom");

  const offset = (page - 1) * limit;

  const { data, isLoading } = useReportsServiceGetReparationReportV2({
    limit,
    offset,
    search,
    startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
  });

  const { mutate: downloadReport, isPending: isDownloadPending } = useReportsServiceDownloadReparationReportV2();

  const company = useSelector((state: any) => state.company);
  const currencySymbol = company?.currencySymbol || "$";

  const columns: ColumnsType<any> = [
    {
      title: "Case ID",
      dataIndex: "reparationCaseId",
      key: "reparationCaseId",
    },
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
    },
    {
      title: "Handler",
      dataIndex: "handlerName",
      key: "handlerName",
    },
    {
      title: "Start Time",
      dataIndex: "startTime",
      key: "startTime",
      render: (val: string) => dayjs(val).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "End Time",
      dataIndex: "endTime",
      key: "endTime",
      render: (val: string) => dayjs(val).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "Duration (hrs)",
      dataIndex: "repairDurationHours",
      key: "repairDurationHours",
      render: (val: number) => (val ? val.toFixed(4) : "-"),
    },
    {
      title: "Total Price",
      dataIndex: "totalPrice",
      key: "totalPrice",
      render: (val: number) => val ? `${currencySymbol}${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `${currencySymbol}0.00`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={status === "PAID" ? "green" : "orange"}>{status}</Tag>
      ),
    },
  ];

  // Period filter logic
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();
    switch (value) {
      case "Today":
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
        break;
      case "This Week":
        setStartDate(now.startOf("week"));
        setEndDate(now.endOf("week"));
        break;
      case "This Month":
        setStartDate(now.startOf("month"));
        setEndDate(now.endOf("month"));
        break;
      case "This Year":
        setStartDate(now.startOf("year"));
        setEndDate(now.endOf("year"));
        break;
      default:
        // Custom
        break;
    }
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date || dayjs());
    setPeriodFilter("Custom");
  };
  const handleEndDateChange = (date: any) => {
    setEndDate(date || dayjs());
    setPeriodFilter("Custom");
  };

  const handleTableChange = (pagination: any) => {
    if (pagination && pagination.current && pagination.pageSize) {
      setPage(pagination.current || 1);
      setLimit(pagination.pageSize || 10);
    }
  };

  const handleDownload = () => {
    downloadReport(
      {
        search,
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      },
      {
        onSuccess: (response: any) => {
          if (response?.downloadUrl) {
            window.open(response.downloadUrl, "_blank");
          }
        },
      }
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, delay: 0 }}
    >
      <AntdSpace direction="vertical" size="large" style={{ width: "100%" }}>
        <AntdSpace style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>Reparation Case Summary</Title>
          <AntdSpace>
            <Input.Search
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by customer, handler, status..."
              style={{ width: 220 }}
            />
            <Select
              value={periodFilter}
              onChange={handlePeriodChange}
              style={{ width: 120 }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              style={{ width: 120 }}
              placeholder="From"
            />
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              style={{ width: 120 }}
              placeholder="To"
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloadPending}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </AntdSpace>
        </AntdSpace>
        {/* KPIs Section */}
        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Repairs"
                value={data?.kpis?.totalRepairs}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Amount"
                value={data?.kpis?.totalAmount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                prefix={currencySymbol}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Avg. Repair Time (hrs)"
                value={data?.kpis?.averageRepairTimeHours?.toFixed(4)}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Avg. Repair Cost"
                value={data?.kpis?.averageRepairCost !== null && data?.kpis?.averageRepairCost !== undefined ? data.kpis.averageRepairCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                prefix={currencySymbol}
                loading={isLoading}
              />
            </Card>
          </Col>
        </Row>
        {/* Main Data Table */}
        <Table
          columns={columns}
          dataSource={data?.items}
          pagination={{
            current: page,
            pageSize: limit,
            total: data?.total || 0,
            onChange: (page, pageSize) => {
              setPage(page);
              setLimit(pageSize);
            },
            pageSizeOptions: [10, 20, 50, 100],
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          rowKey={(item) => item.reparationCaseId}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          loading={isLoading}
        />
        {/* Handler KPI Table */}
        <Card title="Average Handling Time by User" style={{ marginTop: 24 }}>
          <Table
            columns={[
              { title: "Handler", dataIndex: "handlerName", key: "handlerName" },
              {
                title: "Avg. Repair Time (hrs)",
                dataIndex: "averageRepairTimeHours",
                key: "averageRepairTimeHours",
                render: (val: number) => val?.toFixed(4),
              },
              { title: "Repairs Handled", dataIndex: "repairsHandled", key: "repairsHandled" },
            ]}
            dataSource={data?.kpis?.averageHandlingTimeByUser}
            pagination={false}
            rowKey={(item) => item.handlerName}
            size="small"
            loading={isLoading}
          />
        </Card>
      </AntdSpace>
    </motion.div>
  );
};

export default ReparationCaseSummary;
